
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { FileItem } from "../files/types";
import { supabase } from "@/integrations/supabase/client";

// Type for QA items from Supabase
type QAItem = {
  id: string;
  question: string;
  answer: string;
  category: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Categories to match sidebar
export const qaCategories = [
  { value: "documents", label: "Πρότυπες μελέτες" },
  { value: "videos", label: "Εκπαιδευτικά Video" },
  { value: "guides", label: "Goto Οδηγίες" },
  { value: "webinars", label: "Webinars" },
  { value: "qa", label: "Q&A" }
];

export const useQAManagement = () => {
  // Load QA items
  const [qaItems, setQaItems] = useState<FileItem[]>([]);
  const [editingItem, setEditingItem] = useState<FileItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch Q&A items from Supabase
  useEffect(() => {
    fetchQAItems();
  }, []);
  
  const fetchQAItems = async () => {
    setIsLoading(true);
    try {
      // Use type assertion to bypass TypeScript errors
      const response = await (supabase as any)
        .from('qa_items')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (response.error) {
        console.error("Error fetching Q&A items:", response.error);
        toast({
          title: "Σφάλμα",
          description: "Δεν ήταν δυνατή η φόρτωση των ερωτήσεων & απαντήσεων",
          variant: "destructive"
        });
        return;
      }
      
      // Convert Supabase data to FileItem format
      const formattedItems: FileItem[] = response.data.map((item: QAItem) => ({
        id: parseInt(item.id.substring(0, 8), 16), // Generate numeric ID from UUID
        name: item.question.substring(0, 50),
        type: "qa",
        size: "1 KB",
        uploadDate: new Date(item.created_at).toISOString().split('T')[0],
        url: "",
        category: item.category,
        question: item.question,
        answer: item.answer,
        isActive: item.is_active,
        supabaseId: item.id // Keep the original Supabase UUID
      }));
      
      setQaItems(formattedItems);
    } catch (error) {
      console.error("Error in fetchQAItems:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle saving a new Q&A
  const handleSaveQA = async (question: string, answer: string, category: string) => {
    try {
      // Save to Supabase
      const response = await (supabase as any)
        .from('qa_items')
        .insert([
          { question, answer, category }
        ])
        .select();
      
      if (response.error) {
        throw response.error;
      }
      
      if (response.data && response.data.length > 0) {
        const item = response.data[0] as QAItem;
        
        // Convert the new item to FileItem format
        const newItem: FileItem = {
          id: parseInt(item.id.substring(0, 8), 16),
          name: question.substring(0, 50),
          type: "qa",
          size: "1 KB",
          uploadDate: new Date(item.created_at).toISOString().split('T')[0],
          url: "",
          category,
          question,
          answer,
          isActive: item.is_active,
          supabaseId: item.id
        };
        
        // Update local state
        setQaItems(prevItems => [newItem, ...prevItems]);
        
        // Show success message
        toast({
          title: "Επιτυχία",
          description: `Η ερώτηση "${question.substring(0, 30)}..." προστέθηκε επιτυχώς`
        });
      }
    } catch (error) {
      console.error("Error saving Q&A item:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η αποθήκευση της ερώτησης & απάντησης",
        variant: "destructive"
      });
    }
  };
  
  // Handle deleting a Q&A
  const handleDeleteQA = async (id: number) => {
    const itemToDelete = qaItems.find(item => item.id === id);
    if (!itemToDelete || !itemToDelete.supabaseId) {
      toast({
        title: "Σφάλμα",
        description: "Δεν βρέθηκε το αναγνωριστικό του στοιχείου",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await (supabase as any)
        .from('qa_items')
        .delete()
        .eq('id', itemToDelete.supabaseId);
      
      if (response.error) {
        throw response.error;
      }
      
      // Remove from local state
      const updatedQaItems = qaItems.filter(item => item.id !== id);
      setQaItems(updatedQaItems);
      
      // Show success message
      toast({
        title: "Επιτυχία",
        description: `Η ερώτηση "${itemToDelete.question?.substring(0, 30)}..." διαγράφηκε επιτυχώς`
      });
    } catch (error) {
      console.error("Error deleting Q&A item:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η διαγραφή της ερώτησης & απάντησης",
        variant: "destructive"
      });
    }
  };
  
  // Start editing a Q&A
  const startEditingQA = (item: FileItem) => {
    setEditingItem(item);
  };
  
  // Cancel editing
  const cancelEdit = () => {
    setEditingItem(null);
  };
  
  // Toggle active status
  const toggleActiveStatus = async (id: number) => {
    const itemToUpdate = qaItems.find(item => item.id === id);
    if (!itemToUpdate || !itemToUpdate.supabaseId) {
      return;
    }
    
    const newActiveStatus = !(itemToUpdate.isActive ?? true);
    
    try {
      const response = await (supabase as any)
        .from('qa_items')
        .update({ is_active: newActiveStatus })
        .eq('id', itemToUpdate.supabaseId);
      
      if (response.error) {
        throw response.error;
      }
      
      // Update local state
      const updatedQaItems = qaItems.map(item => 
        item.id === id ? { ...item, isActive: newActiveStatus } : item
      );
      setQaItems(updatedQaItems);
      
      // Show success message
      toast({
        title: "Επιτυχία",
        description: `Η ερώτηση ${newActiveStatus ? 'ενεργοποιήθηκε' : 'απενεργοποιήθηκε'} επιτυχώς`
      });
    } catch (error) {
      console.error("Error updating Q&A active status:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η αλλαγή κατάστασης της ερώτησης",
        variant: "destructive"
      });
    }
  };
  
  // Update an existing Q&A
  const handleUpdateQA = async (updatedItem: FileItem) => {
    if (!updatedItem.supabaseId) {
      toast({
        title: "Σφάλμα",
        description: "Δεν βρέθηκε το αναγνωριστικό του στοιχείου",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await (supabase as any)
        .from('qa_items')
        .update({ 
          question: updatedItem.question,
          answer: updatedItem.answer,
          category: updatedItem.category,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedItem.supabaseId);
      
      if (response.error) {
        throw response.error;
      }
      
      // Update local state
      const updatedQaItems = qaItems.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      );
      setQaItems(updatedQaItems);
      
      // Show success message
      toast({
        title: "Επιτυχία",
        description: `Η ερώτηση "${updatedItem.question?.substring(0, 30)}..." ενημερώθηκε επιτυχώς`
      });
      
      // Reset editing state
      setEditingItem(null);
    } catch (error) {
      console.error("Error updating Q&A item:", error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η ενημέρωση της ερώτησης & απάντησης",
        variant: "destructive"
      });
    }
  };

  return {
    qaItems,
    editingItem,
    isLoading,
    setEditingItem,
    handleSaveQA,
    handleDeleteQA,
    handleUpdateQA,
    startEditingQA,
    cancelEdit,
    toggleActiveStatus,
    categories: qaCategories,
  };
};
